import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

interface SpecimenPart {
  id?: number;
  part_code: string;
  part_type: string;
  specimen_part_id?: number;
  collection_method: string;
  collection_datetime: string;
  site: string;
  condition: string;
  submitted_by: string;
  cpt_code: string;
}

interface BarcodeGeneratorProps {
  open: boolean;
  onClose: () => void;
  specimens: SpecimenPart[];
  orderId?: string;
  accessionId?: string;
}

interface BarcodeData {
  specimen: SpecimenPart;
  barcodeType: string;
  barcodeValue: string;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}

const BarcodeGenerator: React.FC<BarcodeGeneratorProps> = ({
  open,
  onClose,
  specimens,
  orderId,
  accessionId,
}) => {
  const [selectedBarcodeType, setSelectedBarcodeType] = useState('CODE128');
  const [barcodeData, setBarcodeData] = useState<BarcodeData[]>([]);

  // Barcode type options
  const barcodeTypes = [
    // 1D Barcodes (Linear)
    { value: 'CODE128', label: 'Code 128 (1D)' },
    { value: 'CODE39', label: 'Code 39 (1D)' },
    { value: 'EAN13', label: 'EAN-13 (1D)' },
    { value: 'UPCA', label: 'UPC-A (1D)' },
    { value: 'ITF14', label: 'ITF-14 (1D)' },
    // 2D Barcodes (Matrix) - Note: JsBarcode doesn't support 2D, would need QR library
    { value: 'QR', label: 'QR Code (2D)' },
    { value: 'DATAMATRIX', label: 'Data Matrix (2D)' },
  ];

  // Generate barcode data for each specimen
  useEffect(() => {
    if (open && specimens.length > 0) {
      const data: BarcodeData[] = specimens.map((specimen, index) => {
        // Create unique barcode value for each specimen
        const barcodeValue = `${accessionId || 'ACC'}-${specimen.part_code}-${specimen.part_type.replace(/\s+/g, '-')}`;
        
        return {
          specimen,
          barcodeType: selectedBarcodeType,
          barcodeValue,
          canvasRef: React.createRef<HTMLCanvasElement>(),
        };
      });
      setBarcodeData(data);
    }
  }, [open, specimens, selectedBarcodeType, accessionId]);

  // Generate barcode on canvas
  useEffect(() => {
    barcodeData.forEach(async (data) => {
      if (data.canvasRef.current) {
        try {
          if (data.barcodeType === 'QR') {
            // Generate QR Code
            await QRCode.toCanvas(data.canvasRef.current, data.barcodeValue, {
              width: 200,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
          } else if (data.barcodeType === 'DATAMATRIX') {
            // For Data Matrix, we'll use QR code as a placeholder since Data Matrix requires a different library
            await QRCode.toCanvas(data.canvasRef.current, data.barcodeValue, {
              width: 200,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
          } else {
            // Generate 1D barcode
            JsBarcode(data.canvasRef.current, data.barcodeValue, {
              format: data.barcodeType,
              width: 2,
              height: 80,
              displayValue: true,
              fontSize: 12,
              margin: 5,
            });
          }
        } catch (error) {
          console.error('Error generating barcode:', error);
        }
      }
    });
  }, [barcodeData]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      // Create SVG barcodes for print to avoid canvas issues
      const svgBarcodes = barcodeData.map((data, index) => {
        try {
          if (data.barcodeType === 'QR') {
            // For QR codes, we'll use a placeholder since SVG generation is complex
            return `<div style="width: 200px; height: 200px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; margin: 10px auto;">
              <span style="font-size: 12px; color: #666;">QR Code: ${data.barcodeValue}</span>
            </div>`;
          } else {
            // Generate SVG barcode for print
            const canvas = document.createElement('canvas');
            JsBarcode(canvas, data.barcodeValue, {
              format: data.barcodeType,
              width: 2,
              height: 80,
              displayValue: true,
              fontSize: 12,
              margin: 5,
            });
            
            // Convert canvas to data URL for print
            const dataUrl = canvas.toDataURL('image/png');
            return `<img src="${dataUrl}" style="max-width: 100%; height: auto; display: block; margin: 10px auto;" alt="Barcode ${index + 1}" />`;
          }
        } catch (error) {
          console.error('Error generating print barcode:', error);
          return `<div style="color: red; text-align: center; padding: 20px;">Error generating barcode</div>`;
        }
      });

      printWindow.document.write(`
        <html>
          <head>
            <title>Specimen Barcodes</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px; 
                background: white;
              }
              .barcode-container { 
                display: inline-block; 
                margin: 10px; 
                padding: 15px; 
                border: 1px solid #ccc; 
                text-align: center;
                page-break-inside: avoid;
                max-width: 300px;
                width: 100%;
                box-sizing: border-box;
              }
              .specimen-info { 
                margin-bottom: 10px; 
                font-size: 12px; 
                font-weight: bold;
                line-height: 1.4;
              }
              .barcode-image {
                max-width: 100%;
                height: auto;
                display: block;
                margin: 10px auto;
              }
              @media print {
                body { margin: 0; }
                .barcode-container { 
                  page-break-inside: avoid; 
                  margin: 5px;
                  border: 1px solid #000;
                }
                .specimen-info {
                  font-size: 10px;
                }
              }
            </style>
          </head>
          <body>
            <h2 style="text-align: center; margin-bottom: 20px;">Specimen Barcodes - Accession ID: ${accessionId || 'N/A'}</h2>
            ${barcodeData.map((data, index) => `
              <div class="barcode-container">
                <div class="specimen-info">
                  Part: ${data.specimen.part_code} | Type: ${data.specimen.part_type}<br>
                  Site: ${data.specimen.site || 'N/A'} | Collection: ${new Date(data.specimen.collection_datetime).toLocaleDateString()}
                </div>
                ${svgBarcodes[index]}
                <div style="font-size: 10px; color: #666; margin-top: 5px;">
                  ${data.barcodeValue}
                </div>
              </div>
            `).join('')}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 1000); // Increased timeout to ensure content is loaded
    }
  };

  const handleDownload = () => {
    // Create a zip file with all barcodes
    // For now, just trigger print which can be saved as PDF
    handlePrint();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Generate Barcodes for {specimens.length} Specimen(s)
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Barcode Type</InputLabel>
            <Select
              value={selectedBarcodeType}
              onChange={(e) => setSelectedBarcodeType(e.target.value)}
              label="Barcode Type"
            >
              {barcodeTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={2}>
          {barcodeData.map((data, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  height: 'fit-content',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Part: {data.specimen.part_code}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {data.specimen.part_type}
                </Typography>
                
                <Box sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  {data.barcodeType === 'QR' || data.barcodeType === 'DATAMATRIX' ? (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'grey.100', 
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 120,
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}>
                      <canvas 
                        ref={data.canvasRef} 
                        style={{ 
                          maxWidth: '100%', 
                          height: 'auto',
                          display: 'block'
                        }} 
                      />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      display: 'flex',
                      justifyContent: 'center'
                    }}>
                      <canvas 
                        ref={data.canvasRef} 
                        style={{ 
                          maxWidth: '100%', 
                          height: 'auto',
                          display: 'block'
                        }} 
                      />
                    </Box>
                  )}
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', wordBreak: 'break-all' }}>
                  {data.barcodeValue}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print Barcodes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeGenerator;